import {
  GiftCoupon,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
  GiftCouponSummary,
  ShareChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftOrderListDateRange } from '@shared/api/giftOrder/giftOrder.hook';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';
import { FeventDate } from '@shared/types';

// ─── 공통 타입 ───────────────────────────────────────

export type DatePreset = '1w' | '2w' | '1m' | '3m' | 'custom';

export type AnalysisTab = 'timing' | 'channel' | 'later';

export interface FeventWithCoffinOut {
  _id: string;
  deceasedInfo?: {
    name?: string;
    coffinOut?: FeventDate;
  };
}

// 구매 시점 분석용
export interface OrderWithDiff {
  dayDiff: number;
  revenue: number;
  quantity: number;
}

export interface BinData {
  label: string;
  range: [number, number];
  count: number;
  revenue: number;
  quantity: number;
  avgRevenue: number;
  avgQuantity: number;
  cumRate: number;
  revenueShare: number;
}

// 발송 채널 분석용
export interface ChannelCount {
  channel: string;
  count: number;
  rate: number;
  label: string;
  color: string;
  hex: string;
}

export interface ChannelStats {
  sentChannels: ChannelCount[];
  total: number;
  sharedTotal: number;
  notSharedTotal: number;
  sharedRate: number;
}

// 수령 시점 분석용 (Summary에 verification populate되는 케이스)
export type CouponWithVerification = GiftCouponSummary &
  Pick<GiftCoupon, 'verification'>;

export interface LaterDayBin {
  label: string;
  range: [number, number]; // 시간 단위 [min inclusive, max exclusive]
  count: number;
  rate: number;
  isUnreceived?: boolean;
}

export interface LaterStats {
  totalVerified: number;
  immediateCount: number;
  laterCount: number;
  laterDeliveredCount: number;
  bins: LaterDayBin[];
}

// ─── 공통 상수 ───────────────────────────────────────

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  '1w': '1주',
  '2w': '2주',
  '1m': '1개월',
  '3m': '3개월',
  custom: '직접선택',
};

export const TABS: { key: AnalysisTab; label: string }[] = [
  { key: 'timing', label: '구매 시점' },
  { key: 'channel', label: '발송 채널' },
  { key: 'later', label: '수령 시점' },
];

export const BAR_COLORS = [
  'bg-emerald-500',
  'bg-indigo-500',
  'bg-indigo-400',
  'bg-blue-500',
  'bg-blue-400',
  'bg-blue-300',
  'bg-slate-400',
  'bg-slate-300',
];

// BAR_COLORS와 동일 팔레트의 hex (SVG stroke·범례용)
export const BAR_HEX = [
  '#10b981',
  '#6366f1',
  '#818cf8',
  '#3b82f6',
  '#60a5fa',
  '#93c5fd',
  '#94a3b8',
  '#cbd5e1',
];

export const BIN_DEFS: { label: string; range: [number, number] }[] = [
  { label: '당일', range: [0, 0] },
  { label: '1일후', range: [1, 1] },
  { label: '2일후', range: [2, 2] },
  { label: '3일후', range: [3, 3] },
  { label: '4~7일후', range: [4, 7] },
  { label: '8~14일후', range: [8, 14] },
  { label: '15~30일후', range: [15, 30] },
  { label: '31일후~', range: [31, Infinity] },
];

export const CHANNEL_KEY_NOT_SHARED = 'notShared';
export const CHANNEL_KEY_UNSPECIFIED = 'channelUnspecified';

export const CHANNEL_CONFIG: Record<
  string,
  { label: string; color: string; hex: string }
> = {
  [ShareChannel.KakaoTalk]: {
    label: '카카오톡',
    color: 'bg-yellow-400',
    hex: '#facc15',
  },
  [ShareChannel.SMS]: { label: '문자', color: 'bg-sky-400', hex: '#38bdf8' },
  [ShareChannel.Other]: {
    label: '다른 앱',
    color: 'bg-violet-400',
    hex: '#a78bfa',
  },
  [CHANNEL_KEY_UNSPECIFIED]: {
    label: '채널 미확인',
    color: 'bg-rose-300',
    hex: '#fda4af',
  },
  [CHANNEL_KEY_NOT_SHARED]: {
    label: '미발송',
    color: 'bg-gray-300',
    hex: '#d1d5db',
  },
};

export const LATER_THRESHOLD_MS = 60 * 60 * 1000; // 1시간

export const LATER_BIN_DEFS: { label: string; range: [number, number] }[] = [
  { label: '당일(<1일)', range: [1, 24] },
  { label: '1일', range: [24, 48] },
  { label: '2~3일', range: [48, 24 * 4] },
  { label: '4~7일', range: [24 * 4, 24 * 8] },
  { label: '8~14일', range: [24 * 8, 24 * 15] },
  { label: '15일~', range: [24 * 15, Infinity] },
];

// ─── 공통 헬퍼 ───────────────────────────────────────

export const getLocalToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const toDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const getDatePreset = (preset: string): GiftOrderListDateRange => {
  const today = getLocalToday();
  const to = toDateStr(today);
  switch (preset) {
    case '1w': {
      const from = new Date(today);
      from.setDate(from.getDate() - 7);
      return { from: toDateStr(from), to };
    }
    case '2w': {
      const from = new Date(today);
      from.setDate(from.getDate() - 14);
      return { from: toDateStr(from), to };
    }
    case '1m': {
      const from = new Date(today);
      from.setMonth(from.getMonth() - 1);
      return { from: toDateStr(from), to };
    }
    case '3m': {
      const from = new Date(today);
      from.setMonth(from.getMonth() - 3);
      return { from: toDateStr(from), to };
    }
    default:
      return {};
  }
};

export const fmt = (n: number): string => n.toLocaleString();

// 최댓값을 "깔끔한 눈금"으로 올림하고 가이드 선 좌표(0 포함)를 만든다.
export const getNiceScale = (
  max: number,
  divisions = 4,
): { niceMax: number; ticks: number[] } => {
  if (max <= 0) {
    return { niceMax: divisions, ticks: [0, divisions] };
  }
  const rawStep = max / divisions;
  const pow = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const fraction = rawStep / pow;
  const niceFraction =
    fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  const step = Math.max(Math.round(niceFraction * pow), 1);
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= niceMax + 1e-9; v += step) {
    ticks.push(v);
  }
  return { niceMax, ticks };
};

// ─── 통계 빌더 ───────────────────────────────────────

export const getOrderWithDiff = (
  order: GiftOrderForAdmin,
): OrderWithDiff | null => {
  const fevent = order.fevent as FeventWithCoffinOut | undefined;
  const coffinOutDate = fevent?.deceasedInfo?.coffinOut?.date;
  const purchasedAt = order.paymentDetail.purchasedAt;

  if (!coffinOutDate || !purchasedAt) return null;

  const coffinOut = new Date(coffinOutDate);
  const purchased = new Date(purchasedAt);

  if (isNaN(coffinOut.getTime()) || isNaN(purchased.getTime())) return null;

  const diffMs = purchased.getTime() - coffinOut.getTime();
  const dayDiff = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const revenue = order.paymentDetail.paymentByCash;
  const quantity = order.orderDetails.reduce((sum, d) => sum + d.quantity, 0);

  return { dayDiff, revenue, quantity };
};

export const buildBins = (items: OrderWithDiff[]): BinData[] => {
  const filtered = items.filter((i) => i.dayDiff >= 0);
  const totalRevenue = filtered.reduce((s, i) => s + i.revenue, 0);

  const bins: BinData[] = BIN_DEFS.map((d) => ({
    ...d,
    count: 0,
    revenue: 0,
    quantity: 0,
    avgRevenue: 0,
    avgQuantity: 0,
    cumRate: 0,
    revenueShare: 0,
  }));

  for (const item of filtered) {
    for (const bin of bins) {
      if (item.dayDiff >= bin.range[0] && item.dayDiff <= bin.range[1]) {
        bin.count++;
        bin.revenue += item.revenue;
        bin.quantity += item.quantity;
        break;
      }
    }
  }

  let cumCount = 0;
  const total = filtered.length;
  for (const bin of bins) {
    bin.avgRevenue = bin.count > 0 ? Math.round(bin.revenue / bin.count) : 0;
    bin.avgQuantity = bin.count > 0 ? bin.quantity / bin.count : 0;
    bin.revenueShare =
      totalRevenue > 0 ? (bin.revenue / totalRevenue) * 100 : 0;
    cumCount += bin.count;
    bin.cumRate = total > 0 ? (cumCount / total) * 100 : 0;
  }

  return bins;
};

export const buildChannelStats = (
  orders: GiftOrderForAdmin[],
): ChannelStats => {
  const counts: Record<string, number> = {};
  let total = 0;
  let sharedTotal = 0;

  for (const order of orders) {
    const coupons = (order.coupons ?? []) as GiftCouponSummary[];
    for (const c of coupons) {
      const isShared = c.shareStatus !== GiftCouponShareStatus.NotShared;
      const rawChannel = c.lastSharedChannel;
      // 레거시 Link 값은 "다른 앱"(Other)으로 흡수
      const normalizedChannel =
        rawChannel === ShareChannel.Link ? ShareChannel.Other : rawChannel;
      const ch = !isShared
        ? CHANNEL_KEY_NOT_SHARED
        : (normalizedChannel ?? CHANNEL_KEY_UNSPECIFIED);
      counts[ch] = (counts[ch] ?? 0) + 1;
      total++;
      if (isShared) sharedTotal++;
    }
  }

  const notSharedTotal = counts[CHANNEL_KEY_NOT_SHARED] ?? 0;
  const sentChannels: ChannelCount[] = [];
  for (const [ch, cnt] of Object.entries(counts)) {
    if (ch === CHANNEL_KEY_NOT_SHARED) continue;
    const cfg = CHANNEL_CONFIG[ch] ?? {
      label: ch,
      color: 'bg-gray-300',
      hex: '#d1d5db',
    };
    sentChannels.push({
      channel: ch,
      count: cnt,
      rate: sharedTotal > 0 ? (cnt / sharedTotal) * 100 : 0,
      ...cfg,
    });
  }
  sentChannels.sort((a, b) => b.count - a.count);

  return {
    sentChannels,
    total,
    sharedTotal,
    notSharedTotal,
    sharedRate: total > 0 ? (sharedTotal / total) * 100 : 0,
  };
};

const isLaterReceive = (c: CouponWithVerification): boolean => {
  if (!c.verification) return false;
  if (c.redeemStatus === GiftCouponRedeemStatus.Available) return true;
  if (c.redeemStatus === GiftCouponRedeemStatus.Delivered) {
    const verifiedAt = c.verification.verifiedAt;
    const requestedAt = c.recipient?.requestedAt;
    if (!verifiedAt || !requestedAt) return false;
    const diffMs = Math.abs(
      new Date(requestedAt).getTime() - new Date(verifiedAt).getTime(),
    );
    return diffMs >= LATER_THRESHOLD_MS;
  }
  return false;
};

export const buildLaterStats = (orders: GiftOrderForAdmin[]): LaterStats => {
  let totalVerified = 0;
  let laterCount = 0;
  let laterDeliveredCount = 0;
  const hours: number[] = [];

  for (const order of orders) {
    const coupons = (order.coupons ?? []) as CouponWithVerification[];
    for (const c of coupons) {
      if (!c.verification) continue;
      totalVerified++;
      if (!isLaterReceive(c)) continue;
      laterCount++;
      if (
        c.redeemStatus === GiftCouponRedeemStatus.Delivered &&
        c.recipient?.requestedAt &&
        c.verification.verifiedAt
      ) {
        laterDeliveredCount++;
        const diffMs = Math.abs(
          new Date(c.recipient.requestedAt).getTime() -
            new Date(c.verification.verifiedAt).getTime(),
        );
        hours.push(diffMs / (60 * 60 * 1000));
      }
    }
  }

  const bins: LaterDayBin[] = LATER_BIN_DEFS.map((d) => ({
    ...d,
    count: 0,
    rate: 0,
  }));
  for (const h of hours) {
    for (const b of bins) {
      if (h >= b.range[0] && h < b.range[1]) {
        b.count++;
        break;
      }
    }
  }
  bins.push({
    label: '미수령',
    range: [Infinity, Infinity],
    count: laterCount - laterDeliveredCount,
    rate: 0,
    isUnreceived: true,
  });
  for (const b of bins) {
    b.rate = laterCount > 0 ? (b.count / laterCount) * 100 : 0;
  }

  return {
    totalVerified,
    immediateCount: totalVerified - laterCount,
    laterCount,
    laterDeliveredCount,
    bins,
  };
};
